/**
 * Rumpfgeometrie, prozedural. Kein externes Asset, keine Texturen: ein Lichterbild
 * ist Geometrie plus Lichterpositionen, und die Geometrie fällt aus wenigen Massen.
 *
 * Koordinaten (Rechtssystem wie in Three.js): Bug nach +Z, oben nach +Y, und damit
 * **Steuerbord nach -X, Backbord nach +X**. Das ist keine Willkür: mit Blick nach
 * vorn (+Z) und oben (+Y) zeigt die rechte Hand nach -X. Wer hier das Vorzeichen
 * dreht, spiegelt die Seitenlichter und macht aus jeder Begegnung die falsche.
 * Alle Masse in Metern.
 */
import {
  BoxGeometry,
  Color,
  BufferGeometry,
  CylinderGeometry,
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  Shape,
  Vector3,
} from 'three'
import type { Mount } from '@/core/types'

export type HullKind = 'cargo' | 'sailing' | 'trawler' | 'tug' | 'pilot' | 'small' | 'barge' | 'seaplane'

export interface VesselModel {
  group: Group
  materials: HullMaterials
  /** Gesetzt, wenn das Modell eigene Punkte für die Seitenlichter mitbringt. */
  sidelightAnchors?: { port: Vector3; stbd: Vector3 }
  /** Geteilte Geometrie aus einem geladenen Modell: nicht freigeben. */
  shared?: boolean
  /** Weltkoordinaten der Montagepunkte relativ zum Rumpfmittelpunkt auf der Wasserlinie. */
  anchors: Record<Mount, Vector3>
  lengthM: number
  beamM: number
  /** Halbe Breite an der Stelle, an der die Seitenlichter sitzen. */
  sidelightOffsetM: number
  deckY: number
  geometries: BufferGeometry[]
}

interface HullDims {
  beamFactor: number
  depthFactor: number
  freeboardFactor: number
  /** Längsanteil, über den der Bug spitz zuläuft. */
  bowFactor: number
  sternFactor: number
}

const DIMS: Record<HullKind, HullDims> = {
  cargo: { beamFactor: 0.15, depthFactor: 0.075, freeboardFactor: 0.045, bowFactor: 0.22, sternFactor: 0.12 },
  sailing: { beamFactor: 0.3, depthFactor: 0.08, freeboardFactor: 0.055, bowFactor: 0.3, sternFactor: 0.22 },
  trawler: { beamFactor: 0.29, depthFactor: 0.12, freeboardFactor: 0.09, bowFactor: 0.26, sternFactor: 0.16 },
  tug: { beamFactor: 0.32, depthFactor: 0.13, freeboardFactor: 0.1, bowFactor: 0.24, sternFactor: 0.16 },
  pilot: { beamFactor: 0.3, depthFactor: 0.1, freeboardFactor: 0.085, bowFactor: 0.28, sternFactor: 0.16 },
  small: { beamFactor: 0.32, depthFactor: 0.09, freeboardFactor: 0.08, bowFactor: 0.32, sternFactor: 0.14 },
  barge: { beamFactor: 0.24, depthFactor: 0.07, freeboardFactor: 0.05, bowFactor: 0.12, sternFactor: 0.08 },
  seaplane: { beamFactor: 0.12, depthFactor: 0.06, freeboardFactor: 0.05, bowFactor: 0.3, sternFactor: 0.25 },
}

/** Decksumriss von oben: spitzer Bug, paralleles Mittelschiff, gerundetes Heck. */
function deckShape(lengthM: number, beamM: number, bowFactor: number, sternFactor: number): Shape {
  const L = lengthM
  const halfB = beamM / 2
  const bow = L / 2
  const stern = -L / 2
  const bowStart = bow - L * bowFactor
  const sternEnd = stern + L * sternFactor
  const s = new Shape()
  s.moveTo(0, bow)
  s.quadraticCurveTo(halfB * 0.75, bow - L * bowFactor * 0.45, halfB, bowStart)
  s.lineTo(halfB, sternEnd)
  s.quadraticCurveTo(halfB, stern, halfB * 0.55, stern)
  s.lineTo(-halfB * 0.55, stern)
  s.quadraticCurveTo(-halfB, stern, -halfB, sternEnd)
  s.lineTo(-halfB, bowStart)
  s.quadraticCurveTo(-halfB * 0.75, bow - L * bowFactor * 0.45, 0, bow)
  return s
}

function hullMesh(
  lengthM: number,
  beamM: number,
  heightM: number,
  dims: HullDims,
  material: MeshStandardMaterial,
): { mesh: Mesh; geometry: BufferGeometry } {
  const shape = deckShape(lengthM, beamM, dims.bowFactor, dims.sternFactor)
  const geometry = new ExtrudeGeometry(shape, {
    depth: heightM,
    bevelEnabled: true,
    bevelThickness: Math.min(0.6, heightM * 0.12),
    bevelSize: Math.min(0.6, beamM * 0.05),
    bevelSegments: 1,
    curveSegments: 6,
  })
  // ExtrudeGeometry baut in der XY-Ebene nach +Z. Nach rotateX liegt das Deck oben,
  // der Bug zeigt dann aber nach -Z; die halbe Drehung um die Hochachse richtet ihn
  // nach +Z aus, wie die Ankerpunkte es erwarten.
  geometry.rotateX(-Math.PI / 2)
  geometry.rotateY(Math.PI)
  geometry.computeVertexNormals()
  const mesh = new Mesh(geometry, material)
  return { mesh, geometry }
}

function box(
  w: number,
  h: number,
  d: number,
  pos: [number, number, number],
  material: MeshStandardMaterial,
): { mesh: Mesh; geometry: BufferGeometry } {
  const geometry = new BoxGeometry(w, h, d)
  const mesh = new Mesh(geometry, material)
  mesh.position.set(pos[0], pos[1], pos[2])
  return { mesh, geometry }
}

function cylinder(
  r: number,
  h: number,
  pos: [number, number, number],
  material: MeshStandardMaterial,
): { mesh: Mesh; geometry: BufferGeometry } {
  const geometry = new CylinderGeometry(r * 0.8, r, h, 8)
  const mesh = new Mesh(geometry, material)
  mesh.position.set(pos[0], pos[1] + h / 2, pos[2])
  return { mesh, geometry }
}

export interface HullMaterials {
  hull: MeshStandardMaterial
  superstructure: MeshStandardMaterial
  mast: MeshStandardMaterial
  /** Instanzmaterialien der GLBs; jedes erhält seine vollständige Tag-/Nachtpalette. */
  surfaces?: { material: MeshStandardMaterial; dayColour: Color; nightColour: Color; dayVertexColors: boolean }[]
}

export function defaultMaterials(): HullMaterials {
  return {
    hull: new MeshStandardMaterial({ color: 0x1d2430, roughness: 0.9, metalness: 0.05 }),
    superstructure: new MeshStandardMaterial({ color: 0x2a3341, roughness: 0.85, metalness: 0.05 }),
    mast: new MeshStandardMaterial({ color: 0x394352, roughness: 0.8, metalness: 0.1 }),
  }
}

/**
 * Baut ein Fahrzeug und liefert seine Montagepunkte.
 * @param hull Rumpfart
 * @param lengthM Länge über alles
 */
export function buildVessel(hull: HullKind, lengthM: number, materials = defaultMaterials()): VesselModel {
  const dims = DIMS[hull]
  const L = lengthM
  const beamM = L * dims.beamFactor
  const depthM = L * dims.depthFactor
  const freeboardM = L * dims.freeboardFactor
  const hullHeight = depthM + freeboardM
  const group = new Group()
  const geometries: BufferGeometry[] = []

  const body = hullMesh(L, beamM, hullHeight, dims, materials.hull)
  body.mesh.position.y = -depthM
  group.add(body.mesh)
  geometries.push(body.geometry)

  const deckY = freeboardM
  let mastFwd = new Vector3(0, deckY + L * 0.09, L * 0.22)
  let mastAft = new Vector3(0, deckY + L * 0.13, -L * 0.18)
  let mastTop = new Vector3(0, deckY + L * 0.14, 0)
  let bestSeen = new Vector3(0, deckY + L * 0.12, 0)
  let yardY = deckY + L * 0.07
  let yardX = beamM * 0.7
  let sidelightOffset = beamM * 0.48

  const add = (m: { mesh: Mesh; geometry: BufferGeometry }): void => {
    group.add(m.mesh)
    geometries.push(m.geometry)
  }

  switch (hull) {
    case 'cargo': {
      const houseW = beamM * 0.8
      const houseH = L * 0.05
      const houseD = L * 0.13
      add(box(houseW, houseH, houseD, [0, deckY + houseH / 2, -L * 0.3], materials.superstructure))
      add(box(houseW * 0.6, houseH * 0.55, houseD * 0.55, [0, deckY + houseH * 1.27, -L * 0.3], materials.superstructure))
      add(cylinder(beamM * 0.11, L * 0.055, [0, deckY + houseH * 1.5, -L * 0.36], materials.superstructure))
      add(cylinder(beamM * 0.035, L * 0.1, [0, deckY, L * 0.22], materials.mast))
      add(cylinder(beamM * 0.035, L * 0.1, [0, deckY + houseH * 1.55, -L * 0.3], materials.mast))
      // Laderaumluken als Andeutung
      for (const z of [0.05, -0.08, 0.18]) {
        add(box(beamM * 0.62, L * 0.012, L * 0.1, [0, deckY + L * 0.006, L * z], materials.superstructure))
      }
      mastFwd = new Vector3(0, deckY + L * 0.105, L * 0.22)
      mastAft = new Vector3(0, deckY + houseH * 1.55 + L * 0.1, -L * 0.3)
      mastTop = mastAft.clone()
      bestSeen = new Vector3(0, deckY + houseH * 1.95, -L * 0.3)
      yardY = deckY + L * 0.075
      yardX = beamM * 0.55
      break
    }
    case 'sailing': {
      const mastH = L * 1.25
      add(cylinder(beamM * 0.035, mastH, [0, deckY, L * 0.06], materials.mast))
      add(box(beamM * 0.08, beamM * 0.08, L * 0.42, [0, deckY + L * 0.09, -L * 0.14], materials.mast))
      add(box(beamM * 0.55, L * 0.03, L * 0.3, [0, deckY + L * 0.015, -L * 0.05], materials.superstructure))
      mastFwd = new Vector3(0, deckY + mastH * 0.45, L * 0.06)
      mastAft = mastFwd.clone()
      mastTop = new Vector3(0, deckY + mastH * 1.02, L * 0.06)
      bestSeen = new Vector3(0, deckY + mastH * 0.75, L * 0.06)
      yardY = deckY + mastH * 0.55
      yardX = beamM * 0.45
      sidelightOffset = beamM * 0.46
      break
    }
    case 'trawler': {
      const houseH = L * 0.1
      add(box(beamM * 0.78, houseH, L * 0.2, [0, deckY + houseH / 2, L * 0.12], materials.superstructure))
      add(cylinder(beamM * 0.06, L * 0.42, [0, deckY + houseH, L * 0.1], materials.mast))
      // Galgen achtern, darauf der Großmast
      add(box(beamM * 0.06, L * 0.16, beamM * 0.06, [beamM * 0.42, deckY, -L * 0.3], materials.mast))
      add(box(beamM * 0.06, L * 0.16, beamM * 0.06, [-beamM * 0.42, deckY, -L * 0.3], materials.mast))
      add(box(beamM * 0.95, beamM * 0.06, beamM * 0.06, [0, deckY + L * 0.16, -L * 0.3], materials.mast))
      add(cylinder(beamM * 0.05, L * 0.34, [0, deckY + L * 0.16, -L * 0.3], materials.mast))
      // Regel 26(b)(ii) verlangt das Topplicht achterlicher UND höher als das grüne
      // Rundumlicht. Deshalb trägt der Trawler achtern den höheren Mast.
      mastFwd = new Vector3(0, deckY + houseH + L * 0.355, L * 0.1)
      mastAft = new Vector3(0, deckY + L * 0.16 + L * 0.355, -L * 0.3)
      mastTop = mastFwd.clone()
      bestSeen = new Vector3(0, deckY + houseH + L * 0.24, L * 0.1)
      yardY = deckY + houseH + L * 0.2
      yardX = beamM * 0.5
      break
    }
    case 'tug': {
      const houseH = L * 0.11
      add(box(beamM * 0.75, houseH, L * 0.22, [0, deckY + houseH / 2, L * 0.08], materials.superstructure))
      add(box(beamM * 0.5, houseH * 0.5, L * 0.1, [0, deckY + houseH * 1.25, L * 0.08], materials.superstructure))
      add(cylinder(beamM * 0.05, L * 0.3, [0, deckY + houseH * 1.5, L * 0.06], materials.mast))
      add(cylinder(beamM * 0.09, L * 0.05, [0, deckY, -L * 0.22], materials.superstructure))
      // Der Schlepper führt seine beiden Topplichter an einem Mast übereinander,
      // deshalb liegt der achtere Punkt auf dem vorderen.
      mastFwd = new Vector3(0, deckY + houseH * 1.5 + L * 0.315, L * 0.06)
      mastAft = mastFwd.clone()
      mastTop = mastFwd.clone()
      bestSeen = new Vector3(0, deckY + houseH * 1.5 + L * 0.19, L * 0.06)
      yardY = deckY + houseH * 1.5 + L * 0.1
      yardX = beamM * 0.45
      break
    }
    case 'pilot': {
      const houseH = L * 0.1
      add(box(beamM * 0.7, houseH, L * 0.26, [0, deckY + houseH / 2, 0], materials.superstructure))
      add(cylinder(beamM * 0.05, L * 0.22, [0, deckY + houseH, 0], materials.mast))
      mastFwd = new Vector3(0, deckY + houseH + L * 0.235, 0)
      mastAft = mastFwd.clone()
      mastTop = mastFwd.clone()
      bestSeen = new Vector3(0, deckY + houseH + L * 0.135, 0)
      yardY = deckY + houseH + L * 0.12
      yardX = beamM * 0.4
      break
    }
    case 'small': {
      add(box(beamM * 0.6, L * 0.06, L * 0.24, [0, deckY + L * 0.03, -L * 0.02], materials.superstructure))
      add(cylinder(beamM * 0.05, L * 0.28, [0, deckY, -L * 0.05], materials.mast))
      mastFwd = new Vector3(0, deckY + L * 0.295, -L * 0.05)
      mastAft = mastFwd.clone()
      mastTop = mastFwd.clone()
      bestSeen = mastFwd.clone()
      yardY = deckY + L * 0.16
      yardX = beamM * 0.4
      break
    }
    case 'barge': {
      add(box(beamM * 0.9, L * 0.02, L * 0.8, [0, deckY + L * 0.01, 0], materials.superstructure))
      mastFwd = new Vector3(0, deckY + L * 0.05, L * 0.4)
      mastAft = new Vector3(0, deckY + L * 0.05, -L * 0.4)
      mastTop = new Vector3(0, deckY + L * 0.06, 0)
      bestSeen = mastTop.clone()
      yardY = deckY + L * 0.05
      yardX = beamM * 0.45
      break
    }
    case 'seaplane': {
      add(box(beamM * 2.6, beamM * 0.12, L * 0.14, [0, deckY + L * 0.07, L * 0.05], materials.superstructure))
      add(box(beamM * 0.9, L * 0.12, beamM * 0.1, [0, deckY + L * 0.06, -L * 0.42], materials.superstructure))
      add(cylinder(beamM * 0.3, L * 0.12, [0, deckY, L * 0.3], materials.superstructure))
      mastFwd = new Vector3(0, deckY + L * 0.13, L * 0.05)
      mastAft = new Vector3(0, deckY + L * 0.16, -L * 0.42)
      mastTop = mastFwd.clone()
      bestSeen = mastFwd.clone()
      yardY = deckY + L * 0.07
      yardX = beamM * 1.25
      sidelightOffset = beamM * 1.25
      break
    }
  }

  // "Wo sie am besten gesehen werden können" (Regeln 27 bis 30) heißt in der Praxis:
  // über allem anderen. Der Punkt liegt deshalb über den Topplichtern, sonst stehen
  // rot-weiß-rot und das Topplicht in einer Reihe und sind nicht zu unterscheiden.
  if (hull !== 'barge' && hull !== 'seaplane') {
    const top = Math.max(mastFwd.y, mastAft.y)
    bestSeen = new Vector3(0, top + Math.max(2.5, L * 0.03), mastFwd.z)
  }

  const anchors: Record<Mount, Vector3> = {
    'mast-fwd': mastFwd,
    'mast-aft': mastAft,
    'masthead-top': mastTop,
    bow: new Vector3(0, deckY + L * 0.012, L * 0.4),
    stern: new Vector3(0, deckY + L * 0.012, -L * 0.46),
    // Das Deck trägt Signalkörper und Handlampen; der Punkt liegt frei über allem,
    // was mittschiffs steht, sonst steckt ein Licht im Aufbau.
    deck: new Vector3(0, deckY + L * 0.09, L * 0.28),
    'yardarm-port': new Vector3(yardX, yardY, 0),
    'yardarm-stbd': new Vector3(-yardX, yardY, 0),
    'best-seen': bestSeen,
  }

  return { group, materials, anchors, lengthM: L, beamM, sidelightOffsetM: sidelightOffset, deckY, geometries }
}

/**
 * Bei Tag ist der Rumpf zu sehen, bei Nacht nur seine Silhouette. Dieselben
 * Materialien, andere Farbwerte: kein zweites Modell, kein zweiter Drawcall.
 */
export function applyDaylight(model: VesselModel, day: boolean): void {
  if (model.materials.surfaces) {
    for (const surface of model.materials.surfaces) {
      surface.material.color.copy(day ? surface.dayColour : surface.nightColour)
      // COLOR_0 bewahrt tagsüber die ursprünglichen Grundfarben bei geteilten
      // Materialien. Nachts bestimmt die Palette den vollständigen Farbwert.
      const vertexColors = day && surface.dayVertexColors
      if (surface.material.vertexColors !== vertexColors) {
        surface.material.vertexColors = vertexColors
        surface.material.needsUpdate = true
      }
    }
    return
  }
  if (day) {
    model.materials.hull.color.setHex(0x6d7886)
    model.materials.superstructure.color.setHex(0xc3cad4)
    model.materials.mast.color.setHex(0x9aa4b0)
  } else {
    model.materials.hull.color.setHex(0x1d2430)
    model.materials.superstructure.color.setHex(0x2a3341)
    model.materials.mast.color.setHex(0x394352)
  }
}

export function disposeVessel(model: VesselModel): void {
  // Geladene Modelle teilen ihre Geometrie über alle Instanzen; nur die selbst
  // gebaute Geometrie gehört dieser Instanz und wird freigegeben.
  if (!model.shared) for (const g of model.geometries) g.dispose()
  for (const surface of model.materials.surfaces ?? []) surface.material.dispose()
  model.group.clear()
}
